/**
 * HomeTab — pinned posts, a taste of the feed, and an at-a-glance sidebar
 * (next event, active challenge, currently reading).
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Trophy, ArrowRight } from 'lucide-react';
import { fadeUp, stagger } from '@/lib/motion';
import { useCommunities } from '../context/CommunitiesContext';
import { formatEventDate } from '../utils/communityHelpers';
import PostCard from '../post/PostCard';
import EmptyState from '../EmptyState';
import BookCoverArt from '@/components/books/BookCoverArt';

export default function HomeTab({ community, role, onTabChange }) {
  const { getPosts, getEvents, getChallenge } = useCommunities();
  const posts = [...getPosts(community.id)].sort((a, b) => (b.pinned - a.pinned) || (b.createdAt - a.createdAt)).slice(0, 4);
  const nextEvent = getEvents(community.id).sort((a, b) => a.datetime - b.datetime)[0];
  const challenge = getChallenge(community.id);

  return (
    <div className="u-container grid gap-8 py-8 lg:grid-cols-[1fr_320px]">
      <div className="min-w-0">
        <h2 className="font-display text-xl font-semibold text-ink">Fresh from the community</h2>
        {posts.length === 0 ? (
          <EmptyState type="discussions" onCTAClick={() => onTabChange('discussions')} />
        ) : (
          <motion.div variants={stagger} initial="hidden" animate="show" className="mt-5 space-y-4">
            {posts.map((post, i) => <motion.div key={post.id} variants={fadeUp} custom={i}><PostCard post={post} communityRole={role} /></motion.div>)}
          </motion.div>
        )}
        <button
          type="button" onClick={() => onTabChange('discussions')}
          className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-coral hover:underline"
        >
          See all discussions <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <aside className="space-y-5">
        {community.currentBook && (
          <div className="card-paper p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-coral">Currently reading</p>
            <div className="mt-2.5 flex items-center gap-3">
              <BookCoverArt book={community.currentBook} className="h-16 w-11 shrink-0 shadow-card" />
              <div className="min-w-0">
                <p className="truncate font-display text-sm font-semibold text-ink">{community.currentBook.title}</p>
                <p className="truncate text-xs text-subtle">{community.currentBook.author}</p>
              </div>
            </div>
          </div>
        )}

        {nextEvent && (
          <div className="card-paper p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-coral"><Calendar className="h-3.5 w-3.5" /> Next up</p>
            <p className="mt-2 font-display text-base font-semibold text-ink">{nextEvent.title}</p>
            <p className="mt-1 text-xs text-subtle">{formatEventDate(nextEvent.datetime)}</p>
            <button type="button" onClick={() => onTabChange('events')} className="mt-3 text-xs font-semibold text-coral hover:underline">View event →</button>
          </div>
        )}

        {challenge && (
          <div className="card-paper p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-coral"><Trophy className="h-3.5 w-3.5" /> Reading challenge</p>
            <p className="mt-2 font-display text-base font-semibold text-ink">{challenge.title}</p>
            <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-line/70">
              <div className="h-full rounded-full bg-gradient-to-r from-coral to-yellow" style={{ width: `${Math.min(100, (challenge.progress / challenge.goal) * 100)}%` }} />
            </div>
            <button type="button" onClick={() => onTabChange('events')} className="mt-3 text-xs font-semibold text-coral hover:underline">View challenge →</button>
          </div>
        )}
      </aside>
    </div>
  );
}
